import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Box, Button, DialogActions, DialogContent, Typography, CircularProgress } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import {
  AutocompleteOption,
  ControllerModal,
  ControllerAutocomplete,
  ControllerAlert as Alert,
  Stack,
} from '@bymason-private/legacy-pattern-components';
import { useAuth } from '@bymason-private/auth-provider';
import { pluralize } from '@bymason/utils';
import { useMoveDevicesToGroup } from '@bymason/controller-api-hooks';

import { useToast } from '../../../../providers';
import { MOVE_DEVICES_MAX_BATCH_COUNT } from '../../../../app-constants';
import MoveDevicesAlert from './move-device-alert';

export default function MoveDevicesModal({ deviceGroupOptions, onClose, open }: MoveDevicesModalProps) {
  const { authToken = '' } = useAuth();
  const apiRef = useGridApiContext();
  const [setToastMessage, setToastSeverity] = useToast();
  const [moveDevices, moveResult, moveError, moveBusy, , , clearMoveResult] = useMoveDevicesToGroup(
    authToken
  );

  const [targetGroup, setTargetGroup] = useState('');
  const moveSubmitted = useRef(false);
  const selectedDeviceIds = Array.from(apiRef.current?.getSelectedRows(), ([key]) => String(key));

  const moveValidationStatus: 'valid' | 'invalid' | null = useMemo(() => {
    if (!moveSubmitted.current && targetGroup) {
      if (!moveBusy && moveResult && !moveError) {
        return 'valid';
      } else if (!moveBusy && moveError) {
        return 'invalid';
      }
    }
    return null;
  }, [moveBusy, moveError, moveResult, targetGroup]);

  const moveSubmissionStatus: 'success' | 'fail' | null = useMemo(() => {
    if (moveSubmitted.current && targetGroup) {
      if (!moveBusy && moveResult && !moveError) {
        return 'success';
      } else if (!moveBusy && moveError) {
        return 'fail';
      }
    }
    return null;
  }, [moveBusy, moveError, moveResult, targetGroup]);

  const resetFormState = useCallback(() => {
    setTargetGroup('');
    clearMoveResult();
    moveSubmitted.current = false;
  }, [clearMoveResult]);

  const handleClose = () => {
    onClose();
  };

  const handleTargetGroupChange = useCallback(
    (evt: unknown, selected: string | AutocompleteOption | null) => {
      const newValue = (typeof selected === 'string' ? selected : selected?.value) || '';

      setTargetGroup(newValue);
      if (newValue) {
        moveDevices({ groupName: newValue, devices: selectedDeviceIds, validateOnly: true });
      } else {
        apiRef.current.setEditRowsModel({});
        clearMoveResult();
      }
    },
    [apiRef, clearMoveResult, moveDevices, selectedDeviceIds]
  );

  const handleSubmit = useCallback(() => {
    if (selectedDeviceIds.length && moveValidationStatus === 'valid') {
      moveSubmitted.current = true;
      moveDevices({ groupName: targetGroup, devices: selectedDeviceIds });
    }
  }, [moveDevices, moveValidationStatus, selectedDeviceIds, targetGroup]);

  useEffect(() => {
    if (moveSubmissionStatus === 'success' && moveResult) {
      setToastSeverity('success');
      setToastMessage(
        pluralize(moveResult.updated)`$count ${['device has', 'devices have']} been moved successfully`
      );
      const gridUpdates = selectedDeviceIds.map((id) => {
        const row = apiRef.current.getRow(id);
        return { ...row, deviceGroup: targetGroup };
      });
      apiRef.current.updateRows(gridUpdates);
      apiRef.current.selectRows([], false, true);
      resetFormState();
      onClose();
    }
  }, [
    apiRef,
    moveError,
    moveResult,
    selectedDeviceIds,
    targetGroup,
    onClose,
    resetFormState,
    setToastMessage,
    setToastSeverity,
    moveValidationStatus,
    moveSubmissionStatus,
  ]);

  useEffect(() => {
    if (moveSubmissionStatus === 'fail') {
      const errorMessage = pluralize(selectedDeviceIds.length)`Failed to move $count ${[
        'device',
        'devices',
      ]} to ${targetGroup} group: ${moveError ?? ''}`;

      if (errorMessage) {
        setToastSeverity('error');
        setToastMessage(errorMessage);
      }
      resetFormState();
    }
  }, [
    moveBusy,
    moveError,
    moveSubmissionStatus,
    moveValidationStatus,
    resetFormState,
    selectedDeviceIds.length,
    setToastMessage,
    setToastSeverity,
    targetGroup,
  ]);

  useEffect(() => {
    if (open) {
      resetFormState();
    }
  }, [open, resetFormState]);

  const enableSubmit =
    moveValidationStatus === 'valid' && selectedDeviceIds.length <= MOVE_DEVICES_MAX_BATCH_COUNT;

  return (
    <ControllerModal onClose={onClose} open={open} title='Move devices'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          {selectedDeviceIds.length > MOVE_DEVICES_MAX_BATCH_COUNT ? (
            <Alert severity='error' variant='outlined'>
              {`No more than ${MOVE_DEVICES_MAX_BATCH_COUNT} devices may be moved at a time.`}
            </Alert>
          ) : (
            <>
              <Typography>
                Choose a group to move <strong>{selectedDeviceIds.length}</strong> selected{' '}
                {pluralize(selectedDeviceIds.length)`${['device', 'devices']}`} to:
              </Typography>
              <ControllerAutocomplete
                AutocompleteProps={{
                  id: 'target-group',
                  options: deviceGroupOptions,
                  onChange: handleTargetGroupChange,
                }}
                label='Target group'
                placeholder='Select a group'
                autoFocus
                required
                fullWidth
              />
            </>
          )}

          {targetGroup && moveBusy && (
            <Box textAlign='center'>
              <CircularProgress color='secondary' />
            </Box>
          )}

          {targetGroup && moveResult && moveValidationStatus === 'valid' && (
            <>
              <MoveDevicesAlert
                moveDevicesResult={moveResult}
                deviceCount={selectedDeviceIds.length}
                targetGroup={targetGroup}
              />
              <Typography>By moving the devices you acknowledge the above alert</Typography>
            </>
          )}

          {moveValidationStatus === 'invalid' && (
            <Alert severity='error' variant='outlined'>
              Validation failed:{' '}
              {moveError || 'You cannot move these devices into this group. Please select a different group.'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={handleClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSubmit}
          size='medium'
          variant='contained'
          disabled={!enableSubmit}
        >
          Move devices
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}

interface MoveDevicesModalProps {
  open: boolean;
  onClose: () => void;
  deviceGroupOptions: {
    value: string;
    label: string;
  }[];
}
